import {
  coalesceMeetingIntelligence,
  getMeetingIntelligenceFromEvent,
  getMeetingIntelligenceStatus,
} from 'shared/utils/meeting_intelligence'

const INCOMPLETE_STATUSES = new Set(['SCHEDULED', 'PRE_JOIN', 'MEETING_IN_PROGRESS'])
const PROCESSING_STATUSES = new Set(['MEETING_ENDED', 'SUMMARIZING'])
const ERROR_STATUSES = new Set([
  'ERROR',
  'CANCELLED',
  'NO_ATTENDEES',
  'EMPTY_TRANSCRIPT',
  'NOT_ENOUGH_CONTENT',
])

export const RecapButtonVariant = Object.freeze({
  Processing: 'processing',
  ViewRecap: 'view_recap',
})

/**
 * Resolves whether the AI recap button should render and which variant to show.
 * @param {Object} event - Meeting event
 * @param {Object} [trackedMeetingIntelligence] - Tracked meeting query result
 * @returns {{ trackedMeetingUuid: string, variant: string } | null}
 */
export const resolveRecapButtonState = (event, trackedMeetingIntelligence) => {
  const eventStatus = getMeetingIntelligenceStatus(event)
  const trackedPayload = trackedMeetingIntelligence?.status
    ? {
      status: trackedMeetingIntelligence.status,
      uuid: trackedMeetingIntelligence.trackedMeetingUuid,
      error: trackedMeetingIntelligence.error,
    }
    : null

  const coalescedIntelligence = coalesceMeetingIntelligence(
    getMeetingIntelligenceFromEvent(event),
    trackedPayload,
  )
  const status = getMeetingIntelligenceStatus({ meetingIntelligence: coalescedIntelligence })
  const trackedMeetingUuid = coalescedIntelligence?.uuid ?? trackedMeetingIntelligence?.trackedMeetingUuid
  const isEventCompleted = eventStatus === 'COMPLETED'
  const isTrackedEnabled = Boolean(trackedMeetingIntelligence?.enabled)

  if (!event || event.cancelled || !trackedMeetingUuid) {
    return null
  }

  if (!isEventCompleted && !isTrackedEnabled) {
    return null
  }

  if (trackedMeetingIntelligence?.deleted) {
    return null
  }

  if (!isEventCompleted && (
    trackedMeetingIntelligence?.error
    || trackedMeetingIntelligence?.trackedDataError
  )) {
    return null
  }

  if (status && INCOMPLETE_STATUSES.has(status)) {
    return null
  }

  if (status && ERROR_STATUSES.has(status) && !isEventCompleted) {
    return null
  }

  if (status === 'COMPLETED' || isEventCompleted) {
    return { trackedMeetingUuid, variant: RecapButtonVariant.ViewRecap }
  }

  if (status && PROCESSING_STATUSES.has(status)) {
    return { trackedMeetingUuid, variant: RecapButtonVariant.Processing }
  }

  if (isTrackedEnabled) {
    return { trackedMeetingUuid, variant: RecapButtonVariant.ViewRecap }
  }

  return null
}

/**
 * Whether the event allows reschedule (solo, not adhoc, policy permits).
 * @param {Object} event - Meeting event
 * @returns {boolean}
 */
export const getShowReschedule = (event) => {
  if (!event) return false
  const isGroup = event.eventType?.kind === 'group'
  const isAdhoc = event.eventType?.type === 'AdhocEventType'
  return !(isGroup || isAdhoc) && Boolean(event.policy?.reschedule)
}
